import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import { track, Events } from '../lib/analytics'
import MovieWheel from '../components/MovieWheel'

export default function Wheel() {
  const { user } = useAuth()
  const { activeGroup } = useGroup()
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [submissions, setSubmissions] = useState([])

  useEffect(() => {
    loadSession()
  }, [user])

  async function loadSession() {
    if (!activeGroup) { navigate('/'); return }

    const { data: activeSession } = await supabase
      .from('movie_night_sessions')
      .select('*')
      .eq('group_id', activeGroup.id)
      .in('status', ['open', 'spinning'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (!activeSession) { navigate('/'); return }
    setSession(activeSession)

    if (activeSession.status === 'open') {
      await supabase
        .from('movie_night_sessions')
        .update({ status: 'spinning' })
        .eq('id', activeSession.id)
    }

    const { data: subs } = await supabase
      .from('session_submissions')
      .select('*, movies(*), profiles!session_submissions_user_id_fkey(display_name)')
      .eq('session_id', activeSession.id)

    const allSubs = subs || []

    // Edge case: everyone picked the *same* movie → skip the spin spectacle.
    // (We check distinct movie IDs, but we DON'T dedupe the wheel itself —
    // if 2 of 6 picked Grand Budapest, it gets 2 slices = double the odds.)
    const distinctMovieIds = new Set(allSubs.map(s => s.movie_id).filter(Boolean))
    if (distinctMovieIds.size === 1 && allSubs.length > 0 && activeSession.status !== 'complete') {
      const only = allSubs[0]
      await supabase
        .from('movie_night_sessions')
        .update({
          winning_movie_id: only.movies.id,
          status: 'complete',
          spun_at: new Date().toISOString(),
        })
        .eq('id', activeSession.id)
      navigate('/result')
      return
    }

    setSubmissions(allSubs)
  }

  async function handleWinner(movie) {
    if (!session) return
    await supabase
      .from('movie_night_sessions')
      .update({
        winning_movie_id: movie.id,
        status: 'complete',
        spun_at: new Date().toISOString(),
      })
      .eq('id', session.id)
    track(Events.WheelSpun, { mode: 'group', picks: submissions.length })
    setTimeout(() => navigate('/result'), 1400)
  }

  if (!session) return null

  if (submissions.length === 0) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-4">
        <p className="text-[var(--fp-text-muted)] mb-4">No picks came through. That's embarrassing.</p>
        <button onClick={() => navigate('/')} className="fp-btn fp-btn-primary max-w-xs">
          Back to Home
        </button>
      </div>
    )
  }

  const movies = submissions.map(s => s.movies)

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-4">
      <MovieWheel
        movies={movies}
        onWinner={handleWinner}
        subtitle={`${movies.length} movie${movies.length !== 1 ? 's' : ''}`}
      />
    </div>
  )
}
